import React from 'react';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import {
    DeferredVariablePaymentType,
    PurchasePriceSummary,
} from '../../../../slices/marketplace/loi-form/term-form';
import Helpers from '../../../../utils/helper';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';

const EMPTY_CELL = '—';

export const formatCell = (value: number | string | null | undefined): string => {
    if (value == null || value === '') {
        return EMPTY_CELL;
    }
    return String(value);
};

export const formatCurrency = (value: number | null | undefined): string => {
    if (value == null || value === 0) {
        return EMPTY_CELL;
    }
    return Helpers.currencyFormat(value, false);
};

export const formatCurrencyAllowZero = (value: number | null | undefined): string => {
    if (value == null) {
        return EMPTY_CELL;
    }
    if (value === 0) {
        return '$0';
    }
    return Helpers.currencyFormat(value, false);
};

export const formatPercent = (
    value: number | null | undefined,
    total: number | null | undefined,
): string => {
    if (value == null || value === 0 || total == null || total === 0) {
        return EMPTY_CELL;
    }
    const pct = (value / total) * 100;
    return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
};

export const formatBoolean = (value: boolean | null | undefined): string => {
    if (value == null) {
        return EMPTY_CELL;
    }
    return value ? 'Yes' : 'No';
};

export interface RowCellValues {
    faceValue: number | null | undefined;
    fairValue: number | null | undefined;
    fairValueExplanation: string | null | undefined;
    fees: number | null | undefined;
    netOfFees: number | null | undefined;
    employeePortion: number | null | undefined;
    netToOwner: number | null | undefined;
    fieldKey: string | null | undefined;
}

export const buildEmptyRowCells = (): RowCellValues => ({
    faceValue: null,
    fairValue: null,
    fairValueExplanation: null,
    fees: null,
    netOfFees: null,
    employeePortion: null,
    netToOwner: null,
    fieldKey: null,
});

export const computeTotalValues = (
    summary: PurchasePriceSummary | null | undefined,
    totalSellerFees: number | null = null,
): RowCellValues => {
    if (!summary) {
        return buildEmptyRowCells();
    }
    const dfpRows = summary.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];

    const cashFace = summary.cashAtCloser?.cashAtClose ?? 0;
    const dfpFace = dfpRows.reduce((s, r) => s + (r.amount ?? 0), 0);
    const dvpFace = dvpRows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    const jvFace = summary.jvEquity?.row?.valueAtClosing ?? 0;
    const corpFace = summary.corporateEquity?.row?.valueAtClosing ?? 0;
    const otherFace = summary.other ?? 0;

    const totalFace = cashFace + dfpFace + dvpFace + jvFace + corpFace + otherFace;
    const totalFairValue =
        (summary.cashAtCloser?.fairValue ?? 0) +
        (dfpRows[0]?.fairValue ?? 0) +
        (dvpRows[0]?.fairValue ?? 0) +
        (summary.jvEquity?.row?.fairValue ?? 0) +
        (summary.corporateEquity?.row?.fairValue ?? 0) +
        (summary.otherFairValue ?? 0);
    const totalEmployee =
        (summary.cashAtCloser?.considerationForEmployees ?? 0) +
        (dfpRows[0]?.considerationForEmployees ?? 0) +
        (dvpRows[0]?.considerationForEmployees ?? 0) +
        (summary.jvEquity?.row?.considerationForEmployees ?? 0) +
        (summary.corporateEquity?.row?.considerationForEmployees ?? 0) +
        (summary.otherConsiderationForEmployees ?? 0);
    const totalNetToOwner =
        (summary.cashAtCloser?.netToSeller ?? 0) +
        (dfpRows[0]?.netToSeller ?? 0) +
        (dvpRows[0]?.netToSeller ?? 0) +
        (summary.jvEquity?.row?.netToSeller ?? 0) +
        (summary.corporateEquity?.row?.netToSeller ?? 0) +
        (summary.otherNetToSeller ?? 0);

    const baseValue = totalFairValue || totalFace || 0;
    const totalNetOfFees =
        baseValue === 0 && totalSellerFees == null
            ? null
            : baseValue - (totalSellerFees ?? 0);

    return {
        faceValue: totalFace || null,
        fairValue: totalFairValue || null,
        fairValueExplanation: null,
        fees: totalSellerFees,
        netOfFees: totalNetOfFees,
        employeePortion: totalEmployee || null,
        netToOwner: totalNetToOwner || null,
        fieldKey: null,
    };
};

export const getFieldComment = (
    dto: LoiComparisonFormDto,
    fieldKey: string | null | undefined,
): string | undefined => {
    if (!fieldKey) return undefined;
    const fieldComments = dto.form?.termForm?.fieldComments ?? dto.form?.fieldComments ?? [];
    return fieldComments.find((c) => c.fieldKey === fieldKey)?.comment;
};

export interface PriceRowProps {
    label: string;
    indented?: boolean;
    cells: RowCellValues;
    totalFaceValue: number;
    totalFairValue: number;
    showFairValue: boolean;
    showFaceColumn: boolean;
    showFairColumn: boolean;
    isBold?: boolean;
    buyerId: number;
    comment?: string;
    isDeepDive?: boolean;
    isClickable?: boolean;
    isExpanded?: boolean;
    onCellsClick?: (buyerId: number) => void;
    tooltipIdSuffix?: string;
}

export const PriceRow = ({
    label,
    indented,
    cells,
    totalFaceValue,
    totalFairValue,
    showFairValue,
    showFaceColumn,
    showFairColumn,
    isBold,
    buyerId,
    comment,
    isDeepDive,
    isClickable,
    isExpanded,
    onCellsClick,
    tooltipIdSuffix = '',
}: PriceRowProps) => {
    const hasFairValueTooltip = !!cells.fairValueExplanation;
    const hasCommentTooltip = !!comment;

    const handleClick = isDeepDive && isClickable && onCellsClick
        ? () => onCellsClick(buyerId)
        : undefined;

    const rowClassName = [
        isBold ? 'total-row' : undefined,
        isDeepDive && isClickable ? 'price-row--clickable' : undefined,
        isDeepDive && isExpanded ? 'price-row--expanded-cells' : undefined,
    ].filter(Boolean).join(' ') || undefined;

    return (
        <tr className={rowClassName} onClick={handleClick}>
            <td className="cell-value">
                <span>{formatCurrency(cells.faceValue)}</span>
                {hasCommentTooltip && (
                    <InfoTooltip
                        text={comment}
                        idText={`comment-${buyerId}-${cells.fieldKey ?? label}${tooltipIdSuffix}`}
                    />
                )}
            </td>
            {showFaceColumn ? (
                <td className="cell-value">{formatPercent(cells.faceValue, totalFaceValue)}</td>
            ) : (
                <td className="percent-expander-col" />
            )}
            {showFairValue && (
                <>
                    <td className="cell-value">
                        <span>{formatCurrency(cells.fairValue)}</span>
                        {hasFairValueTooltip && (
                            <InfoTooltip
                                text={cells.fairValueExplanation!}
                                idText={`fair-value-${buyerId}-${cells.fieldKey ?? label}${tooltipIdSuffix}`}
                            />
                        )}
                    </td>
                    {showFairColumn ? (
                        <td className="cell-value">{formatPercent(cells.fairValue, totalFairValue)}</td>
                    ) : (
                        <td className="percent-expander-col" />
                    )}
                </>
            )}
            <td className="cell-value">{formatCurrencyAllowZero(cells.fees)}</td>
            <td className="cell-value">{formatCurrencyAllowZero(cells.netOfFees)}</td>
            <td className="cell-value">{formatCurrency(cells.employeePortion)}</td>
            <td className="cell-value">{formatCurrency(cells.netToOwner)}</td>
        </tr>
    );
};

const computeNetOfFeesForOtherRow = (
    fairValue: number | null | undefined,
    faceValue: number | null | undefined,
): number | null => fairValue ?? faceValue ?? null;

export const buildCashAtCloseRow = (
    dto: LoiComparisonFormDto,
    totalSellerFees: number | null = null,
): RowCellValues => {
    const cash = dto.form?.termForm?.purchasePriceSummary?.cashAtCloser;
    if (!cash) {
        return { ...buildEmptyRowCells(), fees: totalSellerFees };
    }
    const baseValue = cash.fairValue ?? cash.cashAtClose ?? null;
    const netOfFees = baseValue == null && totalSellerFees == null
        ? null
        : (baseValue ?? 0) - (totalSellerFees ?? 0);
    return {
        faceValue: cash.cashAtClose,
        fairValue: cash.fairValue,
        fairValueExplanation: cash.fairValueExplanation,
        fees: totalSellerFees,
        netOfFees,
        employeePortion: cash.considerationForEmployees,
        netToOwner: cash.netToSeller,
        fieldKey: 'cashAtClose',
    };
};

export const buildDeferredFixedRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredFixedPayments?.rows ?? [];
    const first = rows[0];
    if (!first) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: first.amount,
        fairValue: first.fairValue,
        fairValueExplanation: first.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(first.fairValue, first.amount),
        employeePortion: first.considerationForEmployees,
        netToOwner: first.netToSeller,
        fieldKey: 'deferredFixedPaymentsTotal',
    };
};

export const buildDeferredVariableRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredVariablePayments?.rows ?? [];
    const total = rows.reduce((s, r) => s + (r.valueAtClosing ?? 0), 0);
    const first = rows[0];
    const faceValue = total || null;
    return {
        faceValue,
        fairValue: first?.fairValue,
        fairValueExplanation: first?.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(first?.fairValue, faceValue),
        employeePortion: first?.considerationForEmployees,
        netToOwner: first?.netToSeller,
        fieldKey: 'deferredVariablePaymentsTotal',
    };
};

export const buildEarnoutRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredVariablePayments?.rows ?? [];
    const earnout = rows.find((r) => r.type === DeferredVariablePaymentType.Earnout);
    if (!earnout) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: earnout.valueAtClosing,
        fairValue: earnout.fairValue,
        fairValueExplanation: earnout.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(earnout.fairValue, earnout.valueAtClosing),
        employeePortion: earnout.considerationForEmployees,
        netToOwner: earnout.netToSeller,
        fieldKey: 'earnout',
    };
};

export const buildSyntheticJvRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const rows = dto.form?.termForm?.purchasePriceSummary?.deferredVariablePayments?.rows ?? [];
    const synthetic = rows.find((r) => r.type === DeferredVariablePaymentType.Synthetic);
    if (!synthetic) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: synthetic.valueAtClosing,
        fairValue: synthetic.fairValue,
        fairValueExplanation: synthetic.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(synthetic.fairValue, synthetic.valueAtClosing),
        employeePortion: synthetic.considerationForEmployees,
        netToOwner: synthetic.netToSeller,
        fieldKey: 'syntheticJvEquity',
    };
};

export const buildJvEquityRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const row = dto.form?.termForm?.purchasePriceSummary?.jvEquity?.row;
    if (!row) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: row.valueAtClosing,
        fairValue: row.fairValue,
        fairValueExplanation: row.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(row.fairValue, row.valueAtClosing),
        employeePortion: row.considerationForEmployees,
        netToOwner: row.netToSeller,
        fieldKey: 'jvEquityTotal',
    };
};

export const buildCorporateEquityRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const row = dto.form?.termForm?.purchasePriceSummary?.corporateEquity?.row;
    if (!row) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: row.valueAtClosing,
        fairValue: row.fairValue,
        fairValueExplanation: row.fairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(row.fairValue, row.valueAtClosing),
        employeePortion: row.considerationForEmployees,
        netToOwner: row.netToSeller,
        fieldKey: 'corporateEquityTotal',
    };
};

export const buildOtherRow = (dto: LoiComparisonFormDto): RowCellValues => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    if (!summary) {
        return { ...buildEmptyRowCells(), fees: 0 };
    }
    return {
        faceValue: summary.other,
        fairValue: summary.otherFairValue,
        fairValueExplanation: summary.otherFairValueExplanation,
        fees: 0,
        netOfFees: computeNetOfFeesForOtherRow(summary.otherFairValue, summary.other),
        employeePortion: summary.otherConsiderationForEmployees,
        netToOwner: summary.otherNetToSeller,
        fieldKey: 'other',
    };
};

export const buildTotalValueRow = (
    dto: LoiComparisonFormDto,
    totalSellerFees: number | null = null,
): RowCellValues => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    return computeTotalValues(summary, totalSellerFees);
};

export interface SectionHeaderRowProps {
    label: string;
    colSpan: number;
}

export const SectionHeaderRow = ({ label, colSpan }: SectionHeaderRowProps) => {
    return (
        <tr className="section-header-row">
            <td colSpan={colSpan}>{label}</td>
        </tr>
    );
};
